// User lookup service for calendar sharing
import { collection, query, where, getDocs, doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { createCalendar } from './calendarStorage';

/**
 * Look up a user by their email address
 * Note: This requires that user profiles are stored in a 'users' collection in Firestore
 * You'll need to create this collection when users sign up
 * 
 * @param {string} email - The user's email address
 * @returns {Promise<Object|null>} User object with id and email, or null if not found
 */
export const lookupUserByEmail = async (email) => {
    if (!email) {
        throw new Error('Email is required');
    }

    try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('email', '==', email.toLowerCase().trim()));

        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
            return null;
        }

        // Get first matching user
        const userDoc = querySnapshot.docs[0];
        return {
            id: userDoc.id,
            email: userDoc.data().email,
            displayName: userDoc.data().displayName || null
        };
    } catch (error) {
        console.error('Error looking up user by email:', error);
        throw error;
    }
};

/**
 * Create or update user profile in Firestore
 * Call this when a user signs up or logs in
 * 
 * @param {string} userId - The user's Firebase Auth ID
 * @param {Object} userData - User data (email, displayName, etc.)
 * @returns {Promise<void>}
 */
export const createUserProfile = async (userId, userData) => {
    if (!userId) {
        throw new Error('User ID is required');
    }

    try {
        const userRef = doc(db, 'users', userId);

        // Check if the profile already exists to avoid overwriting userTier on re-login
        const existingSnap = await getDoc(userRef);
        const isNewUser = !existingSnap.exists();

        await setDoc(userRef, {
            email: userData.email?.toLowerCase().trim(),
            displayName: userData.displayName || null,
            photoURL: userData.photoURL || null,
            createdAt: userData.createdAt || new Date(),
            updatedAt: new Date(),
            // Only set userTier on first-time creation; preserve existing value on subsequent logins
            ...(isNewUser && { userTier: 'free' })
        }, { merge: true });

        // Automatically create a "Personal" calendar for new users
        if (isNewUser) {
            try {
                await createCalendar(
                    {
                        name: 'Personal',
                        description: '',
                        color: 'blue'
                    },
                    userId,
                    'free' // Default tier for new users
                );
            } catch (calendarError) {
                // Log the error but don't fail user profile creation
                console.error(`Error creating default Personal calendar for user ${userId}:`, calendarError);
                // Note: User profile creation continues even if calendar creation fails
            }
        }

    } catch (error) {
        console.error('Error creating user profile:', error);
        throw error;
    }
};

/**
 * Get user profile by ID
 * 
 * @param {string} userId - The user's Firebase Auth ID
 * @returns {Promise<Object|null>} User profile or null if not found
 */
export const getUserProfile = async (userId) => {
    if (!userId) {
        throw new Error('User ID is required');
    }

    try {
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);

        if (!userDoc.exists()) {
            return null;
        }

        return {
            id: userDoc.id,
            ...userDoc.data()
        };
    } catch (error) {
        console.error('Error getting user profile:', error);
        throw error;
    }
};

/**
 * Search for users by email (partial match)
 * Useful for autocomplete in share dialogs
 * 
 * @param {string} searchTerm - Partial email to search for
 * @param {number} limit - Maximum number of results
 * @returns {Promise<Array>} Array of matching users
 */
export const searchUsersByEmail = async (searchTerm, limit = 10) => {
    if (!searchTerm || searchTerm.length < 2) {
        return [];
    }

    try {
        const usersRef = collection(db, 'users');
        // Note: Firestore doesn't support full text search or LIKE queries
        // For production, consider using Algolia or similar for better search
        // This is a basic implementation that requires exact prefix matching

        const q = query(
            usersRef,
            where('email', '>=', searchTerm.toLowerCase()),
            where('email', '<=', searchTerm.toLowerCase() + '\uf8ff')
        );

        const querySnapshot = await getDocs(q);
        const users = [];

        querySnapshot.forEach((doc) => {
            if (users.length < limit) {
                users.push({
                    id: doc.id,
                    email: doc.data().email,
                    displayName: doc.data().displayName || null
                });
            }
        });

        return users;
    } catch (error) {
        console.error('Error searching users:', error);
        return [];
    }
};
